/**
 * useContagensPage - Data fetching, filtering and sorting logic for ContagensPage
 *
 * Extracted from ContagensPage to keep it under 500 lines.
 */
import { useState, useEffect, useMemo } from 'react';
import jobService from '../services/jobService';
import { msg } from '../utils/messages';
import { STATUS_FILTER_OPTIONS } from '../constants/status';
import type { FilterOption } from '../components/MultiSelectFilter';
import { usePersistedState } from './usePersistedState';  // N79

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

const formatDateBRLocal = (date: Date): string => {
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${d}/${m}/${date.getFullYear()}`;
};

export const ddmmyyyyToISO = (ddmmyyyy: string): string => {
  const [d, m, y] = ddmmyyyy.split('/');
  return `${y}-${m}-${d}`;
};

export const getDefaultDates = () => {
  const today = new Date();
  const d15ago = new Date(today);
  d15ago.setDate(today.getDate() - 15);
  const d15ahead = new Date(today);
  d15ahead.setDate(today.getDate() + 15);
  return {
    inicio: formatDateBRLocal(d15ago),
    fim: formatDateBRLocal(d15ahead),
  };
};

export function useContagensPage() {
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig[]>([]);

  // Filters — N79: persistidos na sessão para sobreviverem ao "Voltar"
  const [searchTerm, setSearchTerm] = usePersistedState('contagens_f_busca', '');
  const [selectedHospitals, setSelectedHospitals] = usePersistedState<(string | number)[]>('contagens_f_hospitais', []);
  const [selectedClientes, setSelectedClientes] = usePersistedState<(string | number)[]>('contagens_f_clientes', []);
  const [selectedAuditores, setSelectedAuditores] = usePersistedState<(string | number)[]>('contagens_f_auditores', []);
  const [selectedStatus, setSelectedStatus] = usePersistedState<(string | number)[]>('contagens_f_status', []);
  const [showFilters, setShowFilters] = usePersistedState('contagens_f_aberto', false);
  const [allFiltersExpanded, setAllFiltersExpanded] = useState(false);

  const [defaults] = useState(getDefaultDates);
  const [dataInicio, setDataInicio] = usePersistedState<string>('contagens_f_data_inicio', defaults.inicio);
  const [dataFim, setDataFim] = usePersistedState<string>('contagens_f_data_fim', defaults.fim);

  // View mode persisted in localStorage
  const [viewMode, setViewMode] = useState<'list' | 'card'>(() => {
    const saved = localStorage.getItem('contagens_view_mode');
    return saved === 'list' || saved === 'card' ? saved : 'list';
  });

  // Filter options derived from jobs
  const hospitalOptions: FilterOption[] = Array.from(
    new Set(jobs.map(j => JSON.stringify({ value: j.hospital_id, label: j.hospital_nome })))
  ).map((str: string) => JSON.parse(str));

  const clienteOptions: FilterOption[] = Array.from(
    new Set(
      jobs
        .flatMap(j =>
          j.clientes && j.clientes.length > 0
            ? j.clientes
            : j.cliente_id
            ? [{ id: j.cliente_id, razao_social: j.cliente_razao_social, nome_fantasia: j.cliente_nome_fantasia }]
            : []
        )
        .map((c: any) => JSON.stringify({ value: c.id, label: c.nome_fantasia || c.razao_social }))
    )
  ).map((str: string) => JSON.parse(str));

  const auditorOptions: FilterOption[] = Array.from(
    new Set(jobs.map(j => JSON.stringify({ value: j.operador_id, label: j.operador_nome })))
  ).map((str: string) => JSON.parse(str));

  const statusOptions = STATUS_FILTER_OPTIONS;

  // Filtered jobs
  const filteredJobs = jobs.filter(job => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        job.id.toString().includes(term) ||
        job.hospital_nome.toLowerCase().includes(term) ||
        (job.operador_nome && job.operador_nome.toLowerCase().includes(term)) ||
        (job.auditores && job.auditores.some((a: any) => a.nome.toLowerCase().includes(term)));
      if (!matchesSearch) return false;
    }
    if (selectedHospitals.length > 0 && !selectedHospitals.includes(job.hospital_id)) return false;
    if (selectedClientes.length > 0) {
      if (job.clientes && job.clientes.length > 0) {
        const jobClientIds = job.clientes.map((c: any) => c.id);
        if (!jobClientIds.some((id: number) => selectedClientes.includes(id))) return false;
      } else if (job.cliente_id) {
        if (!selectedClientes.includes(job.cliente_id)) return false;
      } else {
        return false;
      }
    }
    if (selectedAuditores.length > 0 && !selectedAuditores.includes(job.operador_id)) return false;
    if (selectedStatus.length > 0 && !selectedStatus.includes(job.status)) return false;
    return true;
  });

  // Sorted jobs
  const sortedJobs = useMemo(() => {
    if (sortConfig.length === 0) return filteredJobs;
    return [...filteredJobs].sort((a, b) => {
      for (const sort of sortConfig) {
        let aVal = a[sort.key];
        let bVal = b[sort.key];
        if (aVal == null && bVal == null) continue;
        if (aVal == null) return sort.direction === 'asc' ? -1 : 1;
        if (bVal == null) return sort.direction === 'asc' ? 1 : -1;
        if (sort.key === 'data_prevista') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        }
        if (sort.key === 'total_items_counted') {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        }
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          const comparison = aVal.toLowerCase().localeCompare(bVal.toLowerCase());
          if (comparison !== 0) return sort.direction === 'asc' ? comparison : -comparison;
        } else {
          if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1;
          if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });
  }, [filteredJobs]);

  // Story 2.60 + HOTFIX 2.60.1: declaracoes movidas para ANTES dos useEffects
  // que as referenciam. Sem isso, o deps array `[..., activeTrashTab]` na
  // linha do useEffect tenta acessar a variavel em Temporal Dead Zone,
  // causando ReferenceError no primeiro render -> contagens "sumiam" pq
  // ContagensPage crashava antes de carregar dados.

  // Story 2.60: estado da aba "Ativas | Excluídas" (lixeira)
  const [activeTrashTab, setActiveTrashTab] = useState<'ativas' | 'excluidas'>('ativas');

  const fetchJobs = async (inicio?: string, fim?: string, includeDeleted?: boolean) => {
    try {
      setLoading(true);
      setError(null);
      const filters: any = { page: 1, page_size: 100 };
      if (inicio) filters.data_inicio = ddmmyyyyToISO(inicio);
      if (fim) filters.data_fim = ddmmyyyyToISO(fim);
      // Story 2.60: incluir flag include_deleted (MASTER only — backend valida)
      if (includeDeleted ?? activeTrashTab === 'excluidas') {
        filters.include_deleted = true;
      }

      const firstPage = await jobService.getJobs(filters);
      let allJobs = [...firstPage.items];

      if (firstPage.total_pages > 1) {
        const remainingPages = Array.from({ length: firstPage.total_pages - 1 }, (_, i) => i + 2);
        const otherPages = await Promise.all(remainingPages.map(page => jobService.getJobs({ ...filters, page })));
        otherPages.forEach(response => { allJobs = [...allJobs, ...response.items]; });
      }
      setJobs(allJobs);
    } catch (error: any) {
      console.error('Erro ao carregar contagens:', error);
      setError(error.message || 'Erro ao carregar contagens');
    } finally {
      setLoading(false);
    }
  };

  // Fetch jobs on date change
  useEffect(() => {
    const inicioValid = !dataInicio || dataInicio.length === 10;
    const fimValid = !dataFim || dataFim.length === 10;
    // Story 2.60: re-fetch quando trocar entre Ativas/Excluidas
    if (inicioValid && fimValid) fetchJobs(dataInicio, dataFim, activeTrashTab === 'excluidas');
  }, [dataInicio, dataFim, activeTrashTab]);

  // Persist viewMode
  useEffect(() => {
    localStorage.setItem('contagens_view_mode', viewMode);
  }, [viewMode]);

  // Auto-dismiss success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const clearAllFilters = () => {
    setSelectedHospitals([]);
    setSelectedClientes([]);
    setSelectedAuditores([]);
    setSelectedStatus([]);
  };

  return {
    jobs,
    loading,
    error,
    successMessage,
    setSuccessMessage,
    sortConfig,
    searchTerm,
    setSearchTerm,
    selectedHospitals,
    setSelectedHospitals,
    selectedClientes,
    setSelectedClientes,
    selectedAuditores,
    setSelectedAuditores,
    selectedStatus,
    setSelectedStatus,
    showFilters,
    setShowFilters,
    allFiltersExpanded,
    setAllFiltersExpanded,
    dataInicio,
    setDataInicio,
    dataFim,
    setDataFim,
    viewMode,
    setViewMode,
    hospitalOptions,
    clienteOptions,
    auditorOptions,
    statusOptions,
    filteredJobs,
    sortedJobs,
    fetchJobs,
    clearAllFilters,
    // Story 2.60: estado da lixeira
    activeTrashTab,
    setActiveTrashTab,
  };
}
